/**
 * i18n dictionary.
 *
 * IMPORTANT — CONTENT GOVERNANCE
 *   Per the approved brief, this file MUST NOT contain any invented
 *   company copy: no history, mission, vision, metrics, certifications,
 *   customer names, testimonials, trade claims. Only structural labels,
 *   navigation strings, and neutral placeholders that describe what
 *   *kind* of content will live in each block.
 *
 *   When the Source of Truth arrives, replace values here — do not
 *   introduce new keys inside pages.
 */

export const en = {
  meta: {
    company: "BNM S.r.l.",
    tagline: "Italian International Trading Company",
    country: "Italy",
    langLabel: "Language",
    switchLang: "Switch language",
  },
  nav: {
    home: "Home",
    about: "About",
    services: "Services",
    commodities: "Commodities",
    compliance: "Compliance & Trust",
    resources: "Resources",
    contact: "Contact",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    skip: "Skip to main content",
  },
  routes: {
    "home": { title: "Home", label: "Home" },
    "about": {
      title: "About BNM",
      label: "About BNM",
      eyebrow: "Company",
      lede: "Italian international trading company.",
    },

    "services.index": {
      title: "Services",
      label: "Services",
      eyebrow: "Capabilities",
      lede: "International trade, sourcing, supply and commercial consulting.",
    },
    "services.trade": {
      title: "International Trade",
      label: "International Trade",
      eyebrow: "Service 01",
      lede: "Cross-border trade of physical commodities under standard international terms.",
    },
    "services.sourcing": {
      title: "Global Sourcing",
      label: "Global Sourcing",
      eyebrow: "Service 02",
      lede: "Origination and qualification of suppliers across multiple geographies.",
    },
    "services.supply": {
      title: "Commodity Supply",
      label: "Commodity Supply",
      eyebrow: "Service 03",
      lede: "Structured supply of grains, food products and petrochemicals to institutional buyers.",
    },
    "services.consulting": {
      title: "International Commercial Consulting",
      label: "Commercial Consulting",
      eyebrow: "Service 04",
      lede: "Advisory on international commercial structures, counterparty due diligence and trade execution.",
    },

    "commodities.index": {
      title: "Commodities",
      label: "Commodities",
      eyebrow: "Sectors",
      lede: "Three sector verticals. Institutional buyers and qualified suppliers.",
    },
    "commodities.grains": {
      title: "Grains & Cereals",
      label: "Grains & Cereals",
      eyebrow: "Sector 01",
      lede: "Wheat, corn, barley, soybeans and derived milling products.",
    },
    "commodities.food": {
      title: "Food Products",
      label: "Food Products",
      eyebrow: "Sector 02",
      lede: "Selected food commodities and ingredients for industrial buyers.",
    },
    "commodities.petrochemical": {
      title: "Petrochemical Products",
      label: "Petrochemical Products",
      eyebrow: "Sector 03",
      lede: "Base petrochemicals and refined derivatives for industrial supply.",
    },

    "compliance.index": {
      title: "Compliance & Trust",
      label: "Compliance & Trust",
      eyebrow: "Governance",
      lede: "Corporate transparency, counterparty verification and anti-fraud posture.",
    },
    "compliance.corporate": {
      title: "Corporate Information",
      label: "Corporate Information",
      eyebrow: "Governance 01",
      lede: "Registered company details and official records.",
    },
    "compliance.kyc": {
      title: "KYC / Company Verification",
      label: "KYC / Verification",
      eyebrow: "Governance 02",
      lede: "How counterparties can verify BNM S.r.l. through official channels.",
    },
    "compliance.antifraud": {
      title: "Anti-Fraud Notice",
      label: "Anti-Fraud Notice",
      eyebrow: "Governance 03",
      lede: "Official warning regarding impersonation and fraudulent solicitations.",
    },

    "resources.index": {
      title: "Resources",
      label: "Resources",
      eyebrow: "Documents",
      lede: "Company profile, downloads and standard trade documents.",
    },
    "resources.profile": {
      title: "Company Profile",
      label: "Company Profile",
      eyebrow: "Document 01",
      lede: "Institutional presentation of BNM S.r.l.",
    },
    "resources.downloads": {
      title: "Downloads",
      label: "Downloads",
      eyebrow: "Document 02",
      lede: "Reference documents available for authorised counterparties.",
    },
    "resources.trade-docs": {
      title: "Trade Documents",
      label: "Trade Documents",
      eyebrow: "Document 03",
      lede: "Standard commercial and shipping document set used in transactions.",
    },

    "contact": {
      title: "Contact",
      label: "Contact",
      eyebrow: "Get in touch",
      lede: "For business inquiries, counterparty verification and commercial requests.",
    },

    "legal.privacy":       { title: "Privacy Policy",         label: "Privacy Policy",         eyebrow: "Legal" },
    "legal.cookies":       { title: "Cookie Policy",          label: "Cookie Policy",          eyebrow: "Legal" },
    "legal.terms":         { title: "Terms of Use",           label: "Terms of Use",           eyebrow: "Legal" },
    "legal.accessibility": { title: "Accessibility Statement",label: "Accessibility",          eyebrow: "Legal" },
    "legal.notice":        { title: "Legal Notice",           label: "Legal Notice",           eyebrow: "Legal" },
  },

  home: {
    heroEyebrow: "Italian International Trading Company",
    heroTitle: "One Italian counterparty across grains, food and petrochemicals.",
    heroSub: "BNM S.r.l. connects institutional buyers and qualified suppliers through a single commercial and documentary interface — registered and independently verifiable in Italy and the EU.",
    heroCta: "Contact us",
    heroCtaSecondary: "Our capabilities",
    statementEyebrow: "Positioning",
    statement: "An Italian trading counterparty for institutional buyers and qualified suppliers, with a focus on clear communication, verified company information and disciplined trade documentation.",
    sectorsEyebrow: "Sectors",
    sectorsTitle: "Three commodity verticals, one counterparty.",
    servicesEyebrow: "Services",
    servicesTitle: "Four capabilities under one counterparty.",
    trustEyebrow: "Compliance",
    trustTitle: "Governance, transparency, verification.",
    trustBody: "Corporate information, KYC verification and an anti-fraud notice are published in the Compliance section, so any counterparty can confirm who they are dealing with before engagement.",
    ctaEyebrow: "Get in touch",
    ctaTitle: "For qualified commercial inquiries.",
    ctaAction: "Open the contact form",
  },

  sections: {
    overviewLabel: "Overview",
    scopeLabel: "Scope",
    counterpartiesLabel: "Counterparties",
    complianceLabel: "Compliance & documentation",
    contactLabel: "Direct contact",
    contentPending: "Content pending official release.",
    contentPendingHint: "This section will be populated from the official Source of Truth.",
    legalReviewPending: "Pending final legal review.",
    legalReviewPendingHint: "This page is a placeholder while the official legal text is under review by counsel. The final approved version will replace this notice.",
    seeAlso: "Related",
    backTo: "Back to",
  },

  contact: {
    formEyebrow: "Contact form",
    formTitle: "Send a commercial inquiry.",
    fields: {
      name: "Full name",
      company: "Company",
      email: "Business email",
      phone: "Phone (optional)",
      country: "Country",
      interest: "Area of interest",
      message: "Message",
      consent: "I have read the Privacy Policy.",
    },
    interests: {
      trade: "International Trade",
      sourcing: "Global Sourcing",
      supply: "Commodity Supply",
      consulting: "Commercial Consulting",
      grains: "Grains & Cereals",
      food: "Food Products",
      petro: "Petrochemical Products",
      other: "Other",
    },
    submit: "Send inquiry",
    sending: "Sending...",
    success: "Inquiry received.",
    successBody: "Your reference number is",
    successHint: "Our commercial team will reply to your business email.",
    errorGeneric: "The inquiry could not be sent. Please try again shortly.",
    errors: {
      required: "This field is required.",
      email: "Enter a valid email address.",
      minLen: "This field is too short.",
      consent: "Please confirm that you have read the Privacy Policy.",
    },
    officeEyebrow: "Registered office",
    officeTitle: "BNM S.R.L.",
    mapsLink: "View on Google Maps",
    channelsEyebrow: "Direct channels",
    emailLabel: "Email",
    phoneLabel: "Phone",
    pecLabel: "Certified email (PEC)",
    websiteLabel: "Website",
    hoursLabel: "Business hours",
    hoursWeekdays: "Monday–Friday, 09:00–18:00",
    hoursWeekend: "Saturday–Sunday, Closed",
    hoursTimezone: "Europe/Rome (CET / CEST)",
  },

  footer: {
    columns: {
      about: "Company",
      capabilities: "Capabilities",
      compliance: "Compliance & Resources",
      legal: "Legal",
    },
    identity: "BNM S.r.l. — Italian International Trading Company.",
    rights: "All rights reserved.",
    languageLabel: "Language",
  },

  notFound: {
    eyebrow: "404",
    title: "Page not found.",
    body: "The requested resource is not available.",
    action: "Return to home",
  },
};

export type Dict = typeof en;

export const it: Dict = {
  meta: {
    company: "BNM S.r.l.",
    tagline: "Società Italiana di Trading Internazionale",
    country: "Italia",
    langLabel: "Lingua",
    switchLang: "Cambia lingua",
  },
  nav: {
    home: "Home",
    about: "Chi siamo",
    services: "Servizi",
    commodities: "Commodities",
    compliance: "Compliance",
    resources: "Risorse",
    contact: "Contatti",
    openMenu: "Apri menu",
    closeMenu: "Chiudi menu",
    skip: "Vai al contenuto principale",
  },
  routes: {
    "home": { title: "Home", label: "Home" },
    "about": {
      title: "Chi siamo",
      label: "Chi siamo",
      eyebrow: "Società",
      lede: "Società italiana di trading internazionale.",
    },

    "services.index": {
      title: "Servizi",
      label: "Servizi",
      eyebrow: "Capacità",
      lede: "Commercio internazionale, sourcing, fornitura di commodities e consulenza commerciale.",
    },
    "services.trade": {
      title: "Commercio Internazionale",
      label: "Commercio Internazionale",
      eyebrow: "Servizio 01",
      lede: "Compravendita transfrontaliera di commodities fisiche secondo termini internazionali standard.",
    },
    "services.sourcing": {
      title: "Global Sourcing",
      label: "Global Sourcing",
      eyebrow: "Servizio 02",
      lede: "Selezione e qualifica di fornitori in molteplici aree geografiche.",
    },
    "services.supply": {
      title: "Fornitura di Commodities",
      label: "Fornitura di Commodities",
      eyebrow: "Servizio 03",
      lede: "Fornitura strutturata di cereali, prodotti alimentari e petrolchimici a buyer istituzionali.",
    },
    "services.consulting": {
      title: "Consulenza Commerciale Internazionale",
      label: "Consulenza Commerciale",
      eyebrow: "Servizio 04",
      lede: "Consulenza su strutture commerciali internazionali, due diligence delle controparti ed esecuzione del trade.",
    },

    "commodities.index": {
      title: "Commodities",
      label: "Commodities",
      eyebrow: "Settori",
      lede: "Tre verticali di commodity. Buyer istituzionali e fornitori qualificati.",
    },
    "commodities.grains": {
      title: "Cereali",
      label: "Cereali",
      eyebrow: "Settore 01",
      lede: "Grano, mais, orzo, soia e prodotti di macinazione.",
    },
    "commodities.food": {
      title: "Prodotti Alimentari",
      label: "Prodotti Alimentari",
      eyebrow: "Settore 02",
      lede: "Commodities alimentari e ingredienti selezionati per buyer industriali.",
    },
    "commodities.petrochemical": {
      title: "Prodotti Petrolchimici",
      label: "Prodotti Petrolchimici",
      eyebrow: "Settore 03",
      lede: "Petrolchimici di base e derivati raffinati per la fornitura industriale.",
    },

    "compliance.index": {
      title: "Compliance",
      label: "Compliance",
      eyebrow: "Governance",
      lede: "Trasparenza societaria, verifica delle controparti e postura anti-frode.",
    },
    "compliance.corporate": {
      title: "Informazioni Societarie",
      label: "Informazioni Societarie",
      eyebrow: "Governance 01",
      lede: "Dati societari e riferimenti ufficiali.",
    },
    "compliance.kyc": {
      title: "KYC / Verifica Società",
      label: "KYC / Verifica",
      eyebrow: "Governance 02",
      lede: "Come le controparti possono verificare BNM S.r.l. attraverso canali ufficiali.",
    },
    "compliance.antifraud": {
      title: "Avviso Anti-Frode",
      label: "Avviso Anti-Frode",
      eyebrow: "Governance 03",
      lede: "Avviso ufficiale in merito a impersonificazioni e sollecitazioni fraudolente.",
    },

    "resources.index": {
      title: "Risorse",
      label: "Risorse",
      eyebrow: "Documenti",
      lede: "Company profile, download e documentazione commerciale standard.",
    },
    "resources.profile": {
      title: "Company Profile",
      label: "Company Profile",
      eyebrow: "Documento 01",
      lede: "Presentazione istituzionale di BNM S.r.l.",
    },
    "resources.downloads": {
      title: "Download",
      label: "Download",
      eyebrow: "Documento 02",
      lede: "Documenti di riferimento disponibili per controparti autorizzate.",
    },
    "resources.trade-docs": {
      title: "Documenti Commerciali",
      label: "Documenti Commerciali",
      eyebrow: "Documento 03",
      lede: "Set standard di documenti commerciali e di spedizione utilizzati nelle transazioni.",
    },

    "contact": {
      title: "Contatti",
      label: "Contatti",
      eyebrow: "Scrivici",
      lede: "Per richieste commerciali, verifica di controparte e informazioni.",
    },

    "legal.privacy":       { title: "Privacy Policy",              label: "Privacy Policy",         eyebrow: "Legale" },
    "legal.cookies":       { title: "Cookie Policy",               label: "Cookie Policy",          eyebrow: "Legale" },
    "legal.terms":         { title: "Termini d'Uso",               label: "Termini d'Uso",          eyebrow: "Legale" },
    "legal.accessibility": { title: "Dichiarazione di Accessibilità",label: "Accessibilità",        eyebrow: "Legale" },
    "legal.notice":        { title: "Note Legali",                 label: "Note Legali",            eyebrow: "Legale" },
  },

  home: {
    heroEyebrow: "Società italiana di trading internazionale",
    heroTitle: "Un'unica controparte italiana per cereali, prodotti alimentari e petrolchimici.",
    heroSub: "BNM S.r.l. mette in contatto buyer istituzionali e fornitori qualificati attraverso un'unica interfaccia commerciale e documentale, registrata e verificabile in modo indipendente in Italia e nell'Unione Europea.",
    heroCta: "Contattaci",
    heroCtaSecondary: "Le nostre capacità",
    statementEyebrow: "Posizionamento",
    statement: "Una controparte di trading italiana per buyer istituzionali e fornitori qualificati, con particolare attenzione alla chiarezza delle comunicazioni, alla verificabilità delle informazioni societarie e al rigore della documentazione commerciale.",
    sectorsEyebrow: "Settori",
    sectorsTitle: "Tre verticali di commodity, un'unica controparte.",
    servicesEyebrow: "Servizi",
    servicesTitle: "Quattro capacità in un'unica controparte.",
    trustEyebrow: "Compliance",
    trustTitle: "Governance, trasparenza, verifica.",
    trustBody: "Le informazioni societarie, la verifica KYC e un avviso anti-frode sono pubblicati nella sezione Compliance, così ogni controparte può confermare con chi sta trattando prima di avviare un rapporto.",
    ctaEyebrow: "Scrivici",
    ctaTitle: "Per richieste commerciali qualificate.",
    ctaAction: "Apri il modulo di contatto",
  },

  sections: {
    overviewLabel: "Panoramica",
    scopeLabel: "Ambito",
    counterpartiesLabel: "Controparti",
    complianceLabel: "Compliance e documentazione",
    contactLabel: "Contatto diretto",
    contentPending: "Contenuto in attesa di pubblicazione ufficiale.",
    contentPendingHint: "Questa sezione sarà popolata dalla Source of Truth ufficiale.",
    legalReviewPending: "In attesa della revisione legale finale.",
    legalReviewPendingHint: "Questa pagina è un segnaposto mentre il testo legale ufficiale è in revisione presso il consulente. La versione finale approvata sostituirà questo avviso.",
    seeAlso: "Correlati",
    backTo: "Torna a",
  },

  contact: {
    formEyebrow: "Modulo di contatto",
    formTitle: "Invia una richiesta commerciale.",
    fields: {
      name: "Nome e cognome",
      company: "Società",
      email: "Email aziendale",
      phone: "Telefono (opzionale)",
      country: "Paese",
      interest: "Area di interesse",
      message: "Messaggio",
      consent: "Ho letto la Privacy Policy.",
    },
    interests: {
      trade: "Commercio Internazionale",
      sourcing: "Global Sourcing",
      supply: "Fornitura di Commodities",
      consulting: "Consulenza Commerciale",
      grains: "Cereali",
      food: "Prodotti Alimentari",
      petro: "Prodotti Petrolchimici",
      other: "Altro",
    },
    submit: "Invia richiesta",
    sending: "Invio in corso...",
    success: "Richiesta ricevuta.",
    successBody: "Il tuo codice di riferimento è",
    successHint: "Il nostro team commerciale risponderà all'email aziendale indicata.",
    errorGeneric: "Non è stato possibile inviare la richiesta. Riprovare a breve.",
    errors: {
      required: "Campo obbligatorio.",
      email: "Inserire un indirizzo email valido.",
      minLen: "Campo troppo breve.",
      consent: "Conferma di aver letto la Privacy Policy.",
    },
    officeEyebrow: "Sede legale",
    officeTitle: "BNM S.R.L.",
    mapsLink: "Apri su Google Maps",
    channelsEyebrow: "Canali diretti",
    emailLabel: "Email",
    phoneLabel: "Telefono",
    pecLabel: "Email certificata (PEC)",
    websiteLabel: "Sito web",
    hoursLabel: "Orari",
    hoursWeekdays: "Lunedì–Venerdì, 09:00–18:00",
    hoursWeekend: "Sabato–Domenica, chiuso",
    hoursTimezone: "Europa/Roma (CET / CEST)",
  },

  footer: {
    columns: {
      about: "Società",
      capabilities: "Capacità",
      compliance: "Compliance e Risorse",
      legal: "Legale",
    },
    identity: "BNM S.r.l. — Società Italiana di Trading Internazionale.",
    rights: "Tutti i diritti riservati.",
    languageLabel: "Lingua",
  },

  notFound: {
    eyebrow: "404",
    title: "Pagina non trovata.",
    body: "La risorsa richiesta non è disponibile.",
    action: "Torna alla home",
  },
};

export const DICTIONARIES = { en, it } as const;
